import { useCallback, useMemo, useState } from "react";
import ExportDropdown from "../components/ExportDropdown";
import TablePage from "./TablePage";

const normalizeText = (value) => (value || "").toString().toLowerCase();

function InvoicePage({ config, rows = [], status, onRowClick, onReload }) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("All");

  const matchesFilter = useCallback(
    (row) => {
      if (!filter || filter.toLowerCase().startsWith("all")) return true;
      const rawStatus =
        row?.invoice?.status ||
        (typeof row?.status === "string" ? row.status : row?.status?.label) ||
        "";
      const normalized = normalizeText(rawStatus);
      const target = normalizeText(filter);
      if (target === "cancelled") return normalized === "cancelled" || normalized === "cancel";
      return normalized === target;
    },
    [filter]
  );

  const matchesQuery = useCallback(
    (row) => {
      const value = normalizeText(query);
      if (!value) return true;
      const item = row?.item || {};
      const textParts = [
        item.title,
        item.subtitle,
        row?.invoice?.invoiceNumber,
        row?.invoice?.customerName,
        row?.invoice?.customerEmail,
        row?.invoice?.customerPhone,
      ].filter(Boolean);
      return textParts.some((part) => normalizeText(part).includes(value));
    },
    [query]
  );

  const filteredRows = useMemo(
    () => rows.filter((row) => matchesFilter(row) && matchesQuery(row)),
    [rows, matchesFilter, matchesQuery]
  );
  const exportFileName = useMemo(() => {
    const iso = new Date().toISOString().split("T")[0];
    return `invoices-${iso}`;
  }, []);
  const exportColumns = useMemo(() => {
    const rest = (config?.columns || [])
      .filter((col) => col.key !== "item")
      .map((col) => ({
        key: col.key,
        label: col.label,
        exportValue: (row) => row[col.key],
      }));
    return [
      {
        key: "customerName",
        label: "Customer Name",
        exportValue: (row) =>
          row?.invoice?.customerName || row?.item?.title || row?.invoice?.customerEmail || "",
      },
      {
        key: "invoiceNumber",
        label: "Invoice Number",
        exportValue: (row) => row?.invoice?.invoiceNumber || row?.item?.subtitle || "",
      },
      ...rest,
    ];
  }, [config?.columns]);

  const renderSearch = () => (
    <div className="table-search">
      <div className="table-search-inner">
        <span className="search-icon" aria-hidden="true">
          🔍
        </span>
        <input
          type="search"
          placeholder="Search by customer or invoice number"
          aria-label="Search by customer or invoice number"
          value={query}
          onChange={(event) => setQuery(event?.target?.value || "")}
        />
      </div>
      {status?.loading ? <div className="muted small">Loading invoices…</div> : null}
    </div>
  );

  return (
    <>
      {status?.error ? (
        <div className="inline-alert error">
          <div>
            <strong>Invoices failed to load.</strong> {status.error}
          </div>
          {status?.onReload ? (
            <button className="ghost-btn small" type="button" onClick={onReload || status.onReload}>
              Retry
            </button>
          ) : null}
        </div>
      ) : null}
      <TablePage
        config={{ ...config, rows: filteredRows }}
        onRowClick={onRowClick}
        hideViewAction
        hideQuickActions
        renderSearch={renderSearch}
        selectedFilters={filter}
        onFilterChange={setFilter}
        actionsRightAligned
        renderActions={() => (
          <ExportDropdown
            rows={filteredRows}
            columns={config?.columns}
            exportColumns={exportColumns}
            fileName={exportFileName}
          />
        )}
      />
    </>
  );
}

export default InvoicePage;
